import { useQuery } from '@apollo/client';
import { ChangeEvent, VFC, useCallback, useMemo, useState } from 'react';

import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  List,
  Toolbar,
} from '@material-ui/core';

import { Application } from '../../common/content-types';
import { useProject } from '../../contexts/project-context';
import { graphql } from '../../generated/gql';
import { ProjectStorageMode } from '../../generated/gql/graphql';
import { useDocuments } from '../../hooks/document/documents';
import { DocumentListItem } from '../../pages/project/document-library/document-list-item';
import AutoComplete from '../auto-complete';
import { MetDialogTitle, withDialog } from '../dialog';
import HighlightedFieldset from '../highlighted-fieldset';
import { Breadcrumb } from '../navigation/breadcrumb';
import { DocumentItem } from './types';

const GET_PROJECTS = graphql(`
  query DocumentsProjects {
    projects(first: 2000) {
      edges {
        node {
          id
          projectKey
          name
          storageMode
        }
      }
    }
  }
`);

const GET_FOLDERS = graphql(`
  query Folders {
    documentFolders {
      root {
        name
        allowUploads
        defaultVisible
        children {
          name
          allowUploads
          defaultVisible
          children {
            name
            allowUploads
            defaultVisible
            children {
              name
              allowUploads
              defaultVisible
            }
          }
        }
      }
    }
  }
`);

interface DocumentMoveDialogProps {
  isCopy: boolean;
  onClose: () => void;
  onConfirm: (targetProjectKey: string, targetPath: string) => void;
}

const DocumentMoveDialogImpl: VFC<DocumentMoveDialogProps> = ({
  isCopy,
  onClose,
  onConfirm,
}: DocumentMoveDialogProps) => {
  const { projectKey: currentProjectKey } = useProject();
  const [targetProjectKey, setTargetProjectKey] = useState(currentProjectKey);
  const [targetPath, setTargetPath] = useState('');
  const { data: projectsData } = useQuery(GET_PROJECTS);

  const handleConfirm = async () => {
    onClose();
    onConfirm(targetProjectKey, targetPath);
  };

  const selectedProject = useMemo(() => {
    return (
      projectsData?.projects?.edges?.find(
        (edge: any) => edge?.node?.projectKey === targetProjectKey
      )?.node || null
    );
  }, [projectsData?.projects?.edges, targetProjectKey]);

  const { data: foldersData, loading: isFoldersLoading } =
    useQuery(GET_FOLDERS);

  const { data: parentItem, loading: isParentItemLoading } = useDocuments(
    targetPath,
    true,
    selectedProject?.storageMode === ProjectStorageMode.Modern
      ? targetProjectKey
      : ''
  );

  const folderItems = useMemo(
    () =>
      parentItem?.children?.filter(
        (item: any) => item?.contentType === Application.MetronFolder
      ),
    [parentItem?.children]
  );

  const legacyRootFolder = useMemo(
    () => foldersData?.documentFolders?.root || null,
    [foldersData?.documentFolders?.root]
  );

  const IsLegacyStorage = useMemo(
    () => selectedProject?.storageMode === ProjectStorageMode.Legacy,
    [selectedProject?.storageMode]
  );

  const mapFolderChildren = useCallback(
    (folder: any, rootPath: string = ''): DocumentItem[] => {
      const normalizePath = (path: string) => path.replace(/^\//, '');
      const currentPath = rootPath ? `${rootPath}/${folder.name}` : folder.name;
      return (
        folder.children?.map(
          (child: any) =>
            ({
              path: normalizePath(currentPath),
              name: child.name,
              contentType: Application.MetronFolder,
              children:
                mapFolderChildren(child, `${rootPath}/${folder.name}`) || [],
              modifiedBy: '',
              modifiedAt: new Date(),
            } satisfies DocumentItem)
        ) || []
      );
    },
    []
  );

  const SubFolders = useMemo(() => {
    if (!IsLegacyStorage || isFoldersLoading) {
      return [];
    }
    const normalizePath = (path: string) => path?.replace(/^\//, '');
    const mappedLegacyRootFolders =
      legacyRootFolder?.map((folder: any) => {
        return {
          path: normalizePath(''),
          name: folder.name,
          contentType: Application.MetronFolder,
          children: mapFolderChildren(folder, '') || [],
          modifiedBy: '',
          modifiedAt: new Date(),
        } satisfies DocumentItem;
      }) || [];

    const findFolder = (folders: DocumentItem[]): DocumentItem | null => {
      const normalizedTargetPath = normalizePath(targetPath);

      for (const folder of folders) {
        if (
          normalizedTargetPath ===
          (folder.path === '' ? folder.name : folder.path + '/' + folder.name)
        ) {
          return folder;
        }

        const matchingFolder = findFolder(folder.children || []);
        if (matchingFolder) {
          return matchingFolder;
        }
      }

      return null;
    };

    const matchingFolder = findFolder(mappedLegacyRootFolders);
    return matchingFolder
      ? matchingFolder.children || []
      : mappedLegacyRootFolders;
  }, [
    legacyRootFolder,
    targetPath,
    isFoldersLoading,
    IsLegacyStorage,
    mapFolderChildren,
  ]);

  const mappedSubFolders = useMemo(
    () =>
      IsLegacyStorage ? (isFoldersLoading ? [] : SubFolders) : folderItems,
    [IsLegacyStorage, SubFolders, folderItems, isFoldersLoading]
  );

  return (
    <>
      <MetDialogTitle onClose={onClose}>
        {isCopy ? 'Kopiera dokument' : 'Flytta dokument'}
      </MetDialogTitle>

      <DialogContent>
        <div style={{ overflow: 'hidden' }}>
          <HighlightedFieldset
            mode='info'
            icon='help'
            title={isCopy ? 'Kopiera filen' : 'Flytta filen'}
            text={`${
              isCopy ? 'Kopiera' : 'Flytta'
            } en fil genom att navigera i filstrukturen nedan till den nya önskade platsen. Filen kan ${
              isCopy ? 'kopieras' : 'flyttas'
            } till ett annat projekt genom att ändra valt projekt nedan. Sök på namn eller projektnummer`}
          />
          <AutoComplete
            id='project-autocomplete'
            label='Sök och välj projekt'
            placeholder='Projektnamn'
            items={
              projectsData?.projects?.edges?.map((p) => ({
                id: p?.node?.projectKey,
                name: `${p?.node?.name} - ${p?.node?.projectKey}`,
                projectKey: p?.node?.projectKey,
              })) || []
            }
            name='project'
            color='primary'
            initialSelectedItem={targetProjectKey ? `${targetProjectKey}` : ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.value !== targetProjectKey) {
                setTargetPath('');
              }
              setTargetProjectKey(e.target.value);
            }}
            anchorEl={null}
          />

          <Divider />
        </div>
        <div style={{ paddingTop: 15, height: 390, overflow: 'auto' }}>
          <Toolbar>
            <Breadcrumb
              folderPath={targetPath ? `/${targetPath}` : ''}
              name='Dokument'
              rootPath={`/project/${targetProjectKey}/documents`}
              moveCopyView={true}
              onSelect={(path) => setTargetPath(path)}
            />
          </Toolbar>
          {(isFoldersLoading || isParentItemLoading) && <CircularProgress />}
          <List>
            {mappedSubFolders?.map((child: any, i: number) => (
              <DocumentListItem
                key={i}
                item={child}
                setAnchorEl={() => {}}
                setSelectedItem={(item) => {
                  setTargetPath(
                    item.path == null || item.path === ''
                      ? item.name
                      : item.path + '/' + item.name
                  );
                }}
                showSelection={false} // Disable selection for folder items
                index={i}
                moveCopyView={true}
              />
            )) || []}
          </List>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button
          color='primary'
          variant='contained'
          disabled={!targetProjectKey || targetPath === ''}
          onClick={() => handleConfirm()}
        >
          Spara
        </Button>
      </DialogActions>
    </>
  );
};

export const DocumentMoveDialog = withDialog(DocumentMoveDialogImpl, {
  maxWidth: 'sm',
});
